'use client';
import { Transition } from '@headlessui/react';
import { LinkIcon } from '@heroicons/react/20/solid';
import { useEffect, useState } from 'react';

import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getRoot, $getSelection, $isRangeSelection } from 'lexical';
import { createPortal } from 'react-dom';
import { buildLinkCommandPayload } from './linkPayload';

export function LinkButton({
    panelHostRef,
    open,
    onRequestToggle,
    onRequestClose,
    visible = true,
    enabled = true,
    onMarkdownSync,
    onRestoreSelection,
}) {
    const [editor] = useLexicalComposerContext();

    const [url, setUrl] = useState('');
    const [newtab, setNewtab] = useState(false);

    useEffect(() => {
        if ((!visible || !enabled) && open) {
            onRequestClose?.();
        }
    }, [visible, enabled, open, onRequestClose]);

    useEffect(() => {
        if (!open) {
            setUrl('');
            setNewtab(false);
        }
    }, [open]);

    if (!visible) return null;

    const expandSelectionToAllIfNone = () => {
        let sel = $getSelection();
        if (!$isRangeSelection(sel) || sel.isCollapsed()) {
            const root = $getRoot();
            root.select(0, root.getTextContentSize());
            sel = $getSelection();
        }
        return sel && $isRangeSelection(sel) ? sel : null;
    };

    const prefillFromSelection = () => {
        editor.update(() => onRestoreSelection?.());
        editor.getEditorState().read(() => {
            const sel = $getSelection();
            if (!sel) return;

            let linkNode = null;
            if ($isRangeSelection(sel)) {
                const nodes = sel.getNodes();
                outer: for (const n of nodes) {
                    let p = n;
                    while (p) {
                        if ($isLinkNode(p)) {
                            linkNode = p;
                            break outer;
                        }
                        p = p.getParent();
                    }
                }
                if (!linkNode) {
                    let p = sel.anchor.getNode();
                    while (p) {
                        if ($isLinkNode(p)) {
                            linkNode = p;
                            break;
                        }
                        p = p.getParent();
                    }
                }
            }
            if (linkNode) {
                const currentUrl = linkNode.getURL?.() || '';
                const tgt = linkNode.getTarget?.() || '';
                const title = linkNode.getTitle?.() || '';
                setUrl(currentUrl);
                setNewtab(tgt === '_blank' || title === 'newtab');
            }
        });
    };

    const handleApply = () => {
        if (!enabled) return;
        const payload = buildLinkCommandPayload(url, newtab);
        if (!payload) return;

        editor.update(() => {
            const restored = onRestoreSelection?.();
            if (restored === false) return;
            const expanded = expandSelectionToAllIfNone();
            if (!expanded) return;
        });

        editor.focus(() => {
            editor.dispatchCommand(TOGGLE_LINK_COMMAND, payload);
        });

        onMarkdownSync?.();
        onRequestClose?.();
    };

    const handleRemove = () => {
        if (!enabled) return;
        editor.update(() => {
            const restored = onRestoreSelection?.();
            if (restored === false) return;
            const expanded = expandSelectionToAllIfNone();
            if (!expanded) return;
        });
        editor.focus(() => editor.dispatchCommand(TOGGLE_LINK_COMMAND, null));
        onMarkdownSync?.();
        onRequestClose?.();
    };

    const handleToggleOpen = () => {
        if (!enabled) return;
        if (!open) prefillFromSelection();
        onRequestToggle?.();
    };

    return (
        <>
            <button
                type="button"
                className="mdx-btn"
                data-active={open ? 'true' : undefined}
                title="Add / remove link"
                aria-label="Add or remove link"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleToggleOpen}
                disabled={!enabled}
                data-disabled={!enabled || undefined}
            >
                <LinkIcon className="h-[14px] w-[14px]" aria-hidden="true" />
            </button>

            {panelHostRef?.current
                ? createPortal(
                      <Transition
                          show={open && enabled}
                          appear
                          enter="transition-all ease-out duration-200"
                          enterFrom="opacity-0 -translate-y-2 max-h-0"
                          enterTo="opacity-100 translate-y-0 max-h-48"
                          leave="transition-all ease-in duration-150"
                          leaveFrom="opacity-100 translate-y-0 max-h-48"
                          leaveTo="opacity-0 -translate-y-2 max-h-0"
                      >
                          <div className="mdx-panel-block">
                              <div className="flex flex-wrap items-center gap-2">
                                  <input
                                      value={url}
                                      onChange={(e) => setUrl(e.target.value)}
                                      placeholder="https://example.com"
                                      className="w-full min-w-[140px] rounded-lg border border-neutral-300 px-2 py-[3px] text-xs text-primary-700 outline-none focus:ring-1 focus:ring-primary-500 sm:w-auto sm:flex-1"
                                      onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                              e.preventDefault();
                                              handleApply();
                                          }
                                      }}
                                      disabled={!enabled}
                                  />

                                  {/* Toggle */}
                                  <label className="relative inline-flex shrink-0 cursor-pointer select-none items-center">
                                      <input
                                          type="checkbox"
                                          className="peer sr-only"
                                          checked={newtab}
                                          onChange={() => setNewtab((v) => !v)}
                                          disabled={!enabled}
                                      />
                                      <div
                                          className="
                        relative h-4 w-7 rounded-full bg-gray-200 transition-colors
                        after:absolute after:left-0.5 after:top-1/2 after:h-3 after:w-3 after:-translate-y-1/2
                        after:translate-x-0 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-transform
                        peer-checked:bg-primary-600 peer-checked:after:translate-x-3 peer-checked:after:border-white
                      "
                                      />
                                      <span className="ml-2 text-[11px] font-medium text-neutral-700">
                                          New tab
                                      </span>
                                  </label>

                                  {/* Actions */}
                                  <div className="flex shrink-0 items-center gap-2">
                                      <button
                                          type="button"
                                          className="flex items-center rounded-lg border border-neutral-700 bg-white/50 px-2 py-[2px] text-xs text-neutral-700 hover:bg-neutral-100 disabled:opacity-50"
                                          onClick={handleRemove}
                                          disabled={!enabled}
                                      >
                                          Remove
                                      </button>
                                      <button
                                          type="button"
                                          className="flex items-center rounded-lg bg-primary-700 px-2 py-[3px] text-xs text-white hover:bg-primary-800 disabled:opacity-50"
                                          onClick={handleApply}
                                          disabled={!enabled}
                                      >
                                          Apply
                                      </button>
                                  </div>
                              </div>
                          </div>
                      </Transition>,
                      panelHostRef.current
                  )
                : null}
        </>
    );
}
